import {testIdProps} from '@github-ui/test-id-props'
import {type RefObject, useEffect, useRef} from 'react'

import styles from './TaillightsCodeView.module.css'

/**
 * A horizontal scrollbar pinned to the bottom of the window while the code is on screen, so long lines can be scrolled
 * without reaching the end of the file. The code keeps scrolling itself natively, and this bar mirrors its position.
 */
export function TaillightsStickyScrollbar({viewportRef}: {viewportRef: RefObject<HTMLElement | null>}) {
  const barRef = useRef<HTMLDivElement>(null)
  const spacerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const bar = barRef.current
    const spacer = spacerRef.current
    const viewport = viewportRef.current
    if (!bar || !spacer || !viewport) return
    // Give both the same scroll range, and hide the bar when the code fits.
    let barTarget: number | null = null
    let viewportTarget: number | null = null
    const measure = () => {
      const range = viewport.scrollWidth - viewport.clientWidth
      const left = viewport.scrollLeft
      bar.toggleAttribute('data-overflowing', range > 0)
      // Relative to the bar's own width, which reads 0 while it's hidden.
      spacer.style.width = `calc(100% + ${range}px)`
      barTarget = left
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Clamps to the new width; runs only on resize.
      bar.scrollLeft = left
    }
    // Measure in the next frame: showing the bar can resize the code again, which would loop inside the observer.
    let frame = 0
    const resize = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    })
    resize.observe(viewport)
    if (viewport.firstElementChild) resize.observe(viewport.firstElementChild)
    // Each side follows the other, skipping only the scroll event its own write causes, so neither bounces back.
    const near = (value: number, target: number | null) => target !== null && Math.abs(value - target) < 1
    const onViewportScroll = () => {
      const left = viewport.scrollLeft
      if (near(left, viewportTarget)) return
      viewportTarget = null
      barTarget = left
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- The only DOM write in this scroll handler.
      bar.scrollLeft = left
    }
    const onBarScroll = () => {
      const left = bar.scrollLeft
      if (near(left, barTarget)) return
      barTarget = null
      viewportTarget = left
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- The only DOM write in this scroll handler.
      viewport.scrollLeft = left
    }
    // eslint-disable-next-line github/prefer-observers -- Mirror a scroll position.
    viewport.addEventListener('scroll', onViewportScroll)
    // eslint-disable-next-line github/prefer-observers -- Mirror a scroll position.
    bar.addEventListener('scroll', onBarScroll)
    return () => {
      cancelAnimationFrame(frame)
      resize.disconnect()
      viewport.removeEventListener('scroll', onViewportScroll)
      bar.removeEventListener('scroll', onBarScroll)
    }
  }, [viewportRef])
  return (
    <div
      {...testIdProps('taillights-sticky-scrollbar')}
      ref={barRef}
      className={styles.StickyScrollbar}
      aria-hidden
      tabIndex={-1}
      // Using the bar keeps focus, and the caret, in the source.
      onMouseDown={event => event.preventDefault()}
    >
      <div ref={spacerRef} className={styles.StickyScrollbarSpacer} />
    </div>
  )
}
